import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { verifyPatient } from '../api/bookingApi';
import LoadingState from './LoadingState';
import BirthdayField from './custom-form/BirthdayField';
import { parseBirthday, type BirthdayParts } from './custom-form/birthday';
import { getLocalToday } from '../utils/date';

interface ExistingPatientStepProps {
  clientId: string;
  phone: string;
  onVerified: (r: { patientToken: string; patientName?: string }) => void | Promise<void>;
  onBack: () => void;
}

export default function ExistingPatientStep({
  clientId,
  phone,
  onVerified,
  onBack,
}: ExistingPatientStepProps) {
  const [parts, setParts] = useState<BirthdayParts>({ month: '', day: '', year: '' });
  const [dobError, setDobError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const birthday = parseBirthday(parts, getLocalToday());
    if (birthday.status !== 'valid') {
      const messages = {
        empty: '"Date of birth" is required.',
        partial: 'Enter the month, day and year for "Date of birth".',
        invalid: '"Date of birth" must be a real date.',
        future: `"Date of birth" can't be in the future.`,
      };
      setDobError(messages[birthday.status]);
      return;
    }
    setDobError('');
    setLoading(true);
    try {
      const result = await verifyPatient(clientId, phone, birthday.iso);
      await onVerified({ patientToken: result.patient_token, patientName: result.patient_name });
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr.status === 401 || apiErr.status === 404) {
        setError('Date of birth does not match our records. Please try again.');
      } else if (apiErr.status === 429) {
        // Rate limited (5/60s per IP).
        setError('Too many attempts. Please wait a moment, or call the office.');
      } else {
        setError(apiErr.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <LoadingState key="loading" type="verification" message="Verifying Identity" />
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-[#ddf3f9] rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Welcome back!</h2>
            <p className="text-gray-500 mt-1">Please confirm your date of birth to continue.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <BirthdayField
                field={{ label: 'Date of birth', required: false }}
                inputId="dob"
                parts={parts}
                onPartChange={(part, value) => {
                  setParts((current) => ({ ...current, [part]: value }));
                  setDobError('');
                }}
                errorId={dobError ? 'dob-error' : undefined}
                className="input-field text-base"
              />
              {dobError && <p id="dob-error" role="alert" className="error-text">{dobError}</p>}
              {error && <p className="error-text">{error}</p>}
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              Verify & Continue
            </button>

            <button type="button" className="btn-secondary" onClick={onBack}>
              Use a different phone number
            </button>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
